import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// eslint-disable-next-line max-len
import { type ConfiguratorApplicationEntrypointContextDataFragment } from '../../../../api/fragments/ConfiguratorApplicationEntrypointContextData';
import { type DebugJourneyDataFragment } from '../../../../api/fragments/DebugJourneyData';
import { ApplicationKind, ApplicationSigningPurpose } from '../../../../api/types';
import ApplicationSessionModal from '../../../../components/ApplicationSessionExpireModal';
import NotFoundResult from '../../../../components/results/NotFoundResult';
import { useThemeComponents } from '../../../../themes';
import useApplicationConcurrencyRedirect from '../../../../utilities/useApplicationConcurrencyRedirect';
import RedirectAndReplace from '../../../shared/RedirectAndReplace';
import useEndSession from '../../../shared/useEndSession';
import useExtendSession from '../../../shared/useExtendSession';
import AdyenPaymentPage from '../../StandardApplicationEntrypoint/AdyenPaymentPage';
import ConfirmationPage from '../../StandardApplicationEntrypoint/ConfirmationPage';
import FiservPaymentPage from '../../StandardApplicationEntrypoint/FiservPaymentPage';
import { JourneyStage } from '../../StandardApplicationEntrypoint/Journey/JourneyController';
import {
    NamiralRedirectPage,
    NamirialPage,
    NamirialRejectPage,
    NamirialTimeoutPage,
} from '../../StandardApplicationEntrypoint/NamirialPage';
import OTPPage from '../../StandardApplicationEntrypoint/OTPPage';
import PayGatePaymentPage from '../../StandardApplicationEntrypoint/PayGatePaymentPage';
import PorschePaymentPage from '../../StandardApplicationEntrypoint/PorschePaymentPage';
import { mayProceedToThanksYou } from '../../StandardApplicationEntrypoint/shared/util';
import TtbPaymentPage from '../../StandardApplicationEntrypoint/TtbPaymentPage';
import ConfiguratorApplication from '../ConfiguratorApplication';
import GuarantorKYC from '../GuarantorKYC';
import InitializeStage from './InitializeStage';
import JourneyLayout from './JourneyLayout';
import { reducer } from './reducer';

export type ConfiguratorApplicationState = Extract<
    DebugJourneyDataFragment['application'],
    { __typename: 'ConfiguratorApplication' }
>;

export type JourneyControllerProps = {
    initialToken: string;
    initialApplication: ConfiguratorApplicationState;
    initialStage: JourneyStage;
    endpoint: ConfiguratorApplicationEntrypointContextDataFragment;
    journeyStages: JourneyStage[];
};

const JourneyController = ({
    initialToken,
    initialApplication,
    initialStage,
    endpoint,
    journeyStages,
}: JourneyControllerProps) => {
    const initialState = useRef({
        token: initialToken,
        stage: initialStage,
        application: initialApplication,
        stages: journeyStages,
    });

    const navigate = useNavigate();
    const [state, dispatch] = useReducer(reducer, initialState.current);
    const locationState = useLocation().state as { token?: string; myInfoAuthorizationCode?: string; linkId?: string };
    const { myInfoAuthorizationCode, linkId } = locationState;

    useApplicationConcurrencyRedirect(initialToken);

    const { ConfiguratorLayout } = useThemeComponents();
    const extendSession = useExtendSession(dispatch);
    const endSession = useEndSession();

    const handleExtendSession = useCallback(async () => {
        await extendSession(state.token, ApplicationKind.Configurator);
    }, [extendSession, state]);
    const handleEndSession = useCallback(
        () => endSession(state.token, ApplicationKind.Configurator),
        [endSession, state]
    );

    useEffect(() => {
        // ensure we locally persist the latest token as well
        navigate('.', { state: { myInfoAuthorizationCode, token: state.token, linkId }, replace: true });
    }, [state.token, navigate, myInfoAuthorizationCode, linkId]);

    const journeyContent = useMemo(() => {
        switch (state.stage) {
            case JourneyStage.Initialize:
                return <InitializeStage dispatch={dispatch} state={state} />;

            case JourneyStage.ApplicantKYC:
                return <ConfiguratorApplication dispatch={dispatch} endpoint={endpoint} state={state} />;

            case JourneyStage.GuarantorKYC:
                return <GuarantorKYC dispatch={dispatch} endpoint={endpoint} state={state} />;

            case JourneyStage.Deposit:
                switch (state.application.deposit?.__typename) {
                    case 'ApplicationAdyenDeposit':
                        return (
                            <AdyenPaymentPage
                                CustomLayout={ConfiguratorLayout}
                                dispatch={dispatch}
                                shouldIncludeLayout={false}
                                state={state}
                            />
                        );

                    case 'ApplicationPorscheDeposit':
                        return (
                            <PorschePaymentPage
                                CustomLayout={ConfiguratorLayout}
                                dispatch={dispatch}
                                shouldIncludeLayout={false}
                                state={state}
                            />
                        );

                    case 'ApplicationFiservDeposit':
                        return (
                            <FiservPaymentPage
                                CustomLayout={ConfiguratorLayout}
                                dispatch={dispatch}
                                shouldIncludeLayout={false}
                                state={state}
                            />
                        );

                    case 'ApplicationPayGateDeposit':
                        return (
                            <PayGatePaymentPage
                                CustomLayout={ConfiguratorLayout}
                                dispatch={dispatch}
                                shouldIncludeLayout={false}
                                state={state}
                            />
                        );

                    case 'ApplicationTtbDeposit':
                        return (
                            <TtbPaymentPage
                                CustomLayout={ConfiguratorLayout}
                                dispatch={dispatch}
                                shouldIncludeLayout={false}
                                state={state}
                            />
                        );

                    default:
                        return <NotFoundResult />;
                }

            case JourneyStage.Otp:
                return (
                    <OTPPage
                        dispatch={dispatch}
                        purpose={ApplicationSigningPurpose.Finance}
                        shouldIncludeLayout={false}
                        state={state}
                    />
                );

            case JourneyStage.InsuranceOtp:
                return (
                    <OTPPage
                        dispatch={dispatch}
                        purpose={ApplicationSigningPurpose.Insurance}
                        shouldIncludeLayout={false}
                        state={state}
                    />
                );

            case JourneyStage.Namirial:
            case JourneyStage.GuarantorNamirial:
                return <NamirialPage purpose={ApplicationSigningPurpose.Finance} state={state} />;

            case JourneyStage.NamirialRedirect:
            case JourneyStage.GuarantorNamirialRedirect:
                return (
                    <NamiralRedirectPage
                        dispatch={dispatch}
                        purpose={ApplicationSigningPurpose.Finance}
                        state={state}
                    />
                );

            case JourneyStage.InsuranceNamirial:
                return <NamirialPage purpose={ApplicationSigningPurpose.Insurance} state={state} />;

            case JourneyStage.InsuranceNamirialRedirect:
                return (
                    <NamiralRedirectPage
                        dispatch={dispatch}
                        purpose={ApplicationSigningPurpose.Insurance}
                        state={state}
                    />
                );

            case JourneyStage.NamirialReject:
            case JourneyStage.GuarantorNamirialReject:
                return <NamirialRejectPage />;

            case JourneyStage.NamirialTimeout:
            case JourneyStage.GuarantorNamirialTimeout:
                return <NamirialTimeoutPage />;

            case JourneyStage.ConfirmEmailSend:
                return <ConfirmationPage />;

            case JourneyStage.Unknown:
            default:
                return <NotFoundResult />;
        }
    }, [ConfiguratorLayout, endpoint, state]);

    const canProceedToThankYou = mayProceedToThanksYou(state.application);

    if (canProceedToThankYou) {
        return <RedirectAndReplace path={{ pathname: '../thankyou' }} state={state} />;
    }

    return (
        <>
            <ApplicationSessionModal
                endSession={handleEndSession}
                expireAt={state.application.expireAt}
                extendSession={handleExtendSession}
            />
            <JourneyLayout dispatch={dispatch} state={state}>
                {journeyContent}
            </JourneyLayout>
        </>
    );
};

export default JourneyController;
